import type { GaxiosError as GaxiosErrorType } from "gaxios";
import { GaxiosError } from "gaxios";

import { OAuth2ErrorCode } from "./oAuthTypes";
import {
  mapHTTPStatusToOAuthError,
  OAUTH_ERROR_MAPPINGS,
} from "./oAuthMappings";

import type { ErrorContext } from "@/errors";
import { OakGoogleClassroomException } from "@/errors";

/**
 * Type guard to check for GaxiosError from Google OAuth2
 */
function isOAuth2Error(error: unknown): error is GaxiosErrorType {
  return error instanceof GaxiosError;
}

function extractOAuthErrorCode(error: GaxiosError): OAuth2ErrorCode {
  const responseError = error.response?.data?.error;
  if (typeof responseError === "string") {
    return responseError as OAuth2ErrorCode;
  }

  // Check error code as GaxiosError can be string or number
  if (typeof error.code === "string") {
    // Network errors: ECONNRESET, ETIMEDOUT, ENOTFOUND, ECONNREFUSED
    if (error.code.startsWith("E")) {
      if (error.code === "ETIMEDOUT") return "timeout_error";
      if (error.code === "ENOTFOUND") return "network_error";
      if (error.code === "ECONNREFUSED") return "network_error";
      return "network_error";
    }
    return error.code as OAuth2ErrorCode;
  }

  if (error.status) {
    return mapHTTPStatusToOAuthError(error.status);
  }

  return "unknown";
}

/**
 * Error handler for Oauth operations
 * Converts errors for more accurate handling and throws custom OakGoogleClassroomException
 *
 * @param error
 * @param context - Context about where/why the error occurred (GaxiosError from google-auth-library)
 * @returns Never (always throws OakGoogleClassroomException)
 */

export function handleGoogleOAuth2Error(
  error: unknown,
  context: ErrorContext,
): never {
  console.error(`[${context.service}] Error in ${context.operation}:`, error);

  let errorCode: OAuth2ErrorCode | undefined;
  let originalError: Error;

  if (isOAuth2Error(error)) {
    errorCode = extractOAuthErrorCode(error);
    originalError = error;
  } else if (error instanceof Error) {
    originalError = error;
    errorCode = "unknown";
  } else {
    originalError = new Error(String(error));
    errorCode = "unknown";
  }

  const oAuth2ErrorMapping =
    errorCode && errorCode in OAUTH_ERROR_MAPPINGS
      ? OAUTH_ERROR_MAPPINGS[errorCode]
      : OAUTH_ERROR_MAPPINGS.unknown;

  console.error(
    `[Internal] Error from Google OAuth2: ${oAuth2ErrorMapping.internalMessage}`,
    {
      code: errorCode,
      operation: context.operation,
      metadata: context.metadata,
    },
  );

  throw new OakGoogleClassroomException(
    oAuth2ErrorMapping.userMessage,
    context.service,
    {
      code: errorCode,
      shouldRetry: oAuth2ErrorMapping.shouldRetry,
      severity: oAuth2ErrorMapping.severity,
      context,
      originalError,
    },
  );
}
